import { setAutoTopUpEnabled } from '@saaga/shared/store/slices/billing.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { microsToDollars } from '@saaga/shared/utils/billing.util'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { BillingAutoTopUpSettings } from '@/components/billing/billing.interface'

const DEFAULT_AUTO_TOP_UP: BillingAutoTopUpSettings = {
  isEnabled: false,
  threshold: 5,
  topUpAmount: 50,
}

interface AutoTopUpConfig {
  thresholdAmount: string
  topUpAmount: string
  enabled: boolean
}

interface AutoTopUpUpsertConfig {
  threshold: number
  topUpAmount: number
  isEnabled: boolean
}

export interface UseAutoTopUpStateParams {
  getConfig: () => Promise<AutoTopUpConfig | null>
  upsertConfig: (config: AutoTopUpUpsertConfig) => Promise<unknown>
  deleteConfig: () => Promise<unknown>
}

export interface UseAutoTopUpStateReturn {
  autoTopUpSettings: BillingAutoTopUpSettings
  isPendingEnable: boolean
  isLoading: boolean
  handleAutoTopUpToggle: (isEnabled: boolean) => Promise<void>
  handleAutoTopUpSave: (data: { threshold: number; topUpAmount: number }) => Promise<void>
}

const mapConfigToSettings = (config: AutoTopUpConfig): BillingAutoTopUpSettings => ({
  isEnabled: config.enabled,
  threshold: microsToDollars(config.thresholdAmount),
  topUpAmount: microsToDollars(config.topUpAmount),
})

const isNotFoundError = (error: unknown): boolean =>
  error instanceof Error && error.message.toLowerCase().includes('not found')

interface StateRefs {
  savedSettingsRef: React.RefObject<BillingAutoTopUpSettings>
  setLocalSettings: React.Dispatch<React.SetStateAction<BillingAutoTopUpSettings>>
  dispatchAutoTopUp: (isEnabled: boolean) => void
}

const executeDisable = async (
  deleteConfig: () => Promise<unknown>,
  refs: StateRefs
): Promise<void> => {
  try {
    await deleteConfig()
  } catch (error) {
    if (!isNotFoundError(error)) {
      toast.error('Failed to disable auto top-up')
      return
    }
  }
  const resetSettings = { ...refs.savedSettingsRef.current, isEnabled: false }
  refs.savedSettingsRef.current = resetSettings
  refs.setLocalSettings(resetSettings)
  refs.dispatchAutoTopUp(false)
  trackEvent('auto_top_up_toggled', { isEnabled: false })
  toast.info('Auto top-up disabled')
}

const executeSave = async (
  upsertConfig: (config: AutoTopUpUpsertConfig) => Promise<unknown>,
  data: { threshold: number; topUpAmount: number },
  refs: StateRefs
): Promise<void> => {
  try {
    await upsertConfig({
      threshold: data.threshold,
      topUpAmount: data.topUpAmount,
      isEnabled: true,
    })
    const newSettings: BillingAutoTopUpSettings = { isEnabled: true, ...data }
    refs.savedSettingsRef.current = newSettings
    refs.setLocalSettings(newSettings)
    refs.dispatchAutoTopUp(true)
    trackEvent('auto_top_up_toggled', { isEnabled: true })
    trackEvent('auto_top_up_configured', {
      threshold: data.threshold,
      topUpAmount: data.topUpAmount,
    })
    toast.success('Auto top-up settings saved')
  } catch {
    toast.error('Failed to save auto top-up settings')
  }
}

export const useAutoTopUpState = ({
  getConfig,
  upsertConfig,
  deleteConfig,
}: UseAutoTopUpStateParams): UseAutoTopUpStateReturn => {
  const dispatch = useAppDispatch()
  const [localSettings, setLocalSettings] = useState<BillingAutoTopUpSettings>(DEFAULT_AUTO_TOP_UP)
  const savedSettingsRef = useRef<BillingAutoTopUpSettings>(DEFAULT_AUTO_TOP_UP)
  const [isLoading, setIsLoading] = useState(true)
  const dispatchAutoTopUp = useCallback(
    (isEnabled: boolean) => dispatch(setAutoTopUpEnabled(isEnabled)),
    [dispatch]
  )
  const refs: StateRefs = { savedSettingsRef, setLocalSettings, dispatchAutoTopUp }

  useEffect(() => {
    const fetchConfig = async (): Promise<void> => {
      setIsLoading(true)
      try {
        const config = await getConfig()
        if (config) {
          const mapped = mapConfigToSettings(config)
          setLocalSettings(mapped)
          savedSettingsRef.current = mapped
          dispatchAutoTopUp(mapped.isEnabled)
        }
      } finally {
        setIsLoading(false)
      }
    }
    void fetchConfig()
  }, [getConfig, dispatchAutoTopUp])

  const handleToggle = useCallback(
    async (isEnabled: boolean): Promise<void> => {
      if (isEnabled) return setLocalSettings((prev) => ({ ...prev, isEnabled: true }))
      await executeDisable(deleteConfig, refs)
    },
    [deleteConfig, refs]
  )

  const handleSave = useCallback(
    (data: { threshold: number; topUpAmount: number }) => executeSave(upsertConfig, data, refs),
    [upsertConfig, refs]
  )

  return {
    autoTopUpSettings: localSettings,
    isPendingEnable: localSettings.isEnabled && !savedSettingsRef.current.isEnabled,
    isLoading,
    handleAutoTopUpToggle: handleToggle,
    handleAutoTopUpSave: handleSave,
  }
}
